import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { Icon } from '@components/icon/Icon';
import { ImageUploader } from '@shared/components/imageUploader/ImageUploader';
import { getRestaurantImagePath } from '@shared/utils/getRestaurantImagePath';
import type {
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { getEventImageUploadUrl } from '../../apiHelpers';
import type { EventFormData } from '../RestaurantEventForm';
import styles from './PhotosStep.scss';

export const PhotosStep = ({
  getValues,
  setValue,
  watch,
}: {
  getValues: UseFormGetValues<EventFormData>;
  setValue: UseFormSetValue<EventFormData>;
  watch: UseFormWatch<EventFormData>;
}) => {
  const { id: restaurantId } = useRestaurant();
  const images = watch('images');
  const previewUrls =
    images?.map((img) => getRestaurantImagePath(img.imageKey)) ?? [];

  const handleImageUpload = (key: string) => {
    const current = getValues('images') ?? [];
    setValue('images', [{ imageKey: key }, ...current], { shouldDirty: true });
  };

  const handleRemoveImagesAt = (indices: number[]) => {
    if (indices.length === 0) return;
    const toRemove = new Set(indices);
    const current = getValues('images') ?? [];
    setValue(
      'images',
      current.filter((_, i) => !toRemove.has(i)),
      { shouldDirty: true },
    );
  };

  const getUploadUrl = () =>
    getEventImageUploadUrl(restaurantId).then(
      ({ signedUploadImageUrl, objectKey }) => ({
        signedUploadUrl: signedUploadImageUrl,
        objectKey,
      }),
    );

  return (
    <section>
      <p className={styles.description}>Add images to showcase your event</p>
      <ImageUploader
        imageUrl={null}
        imageUrls={previewUrls}
        photosHeading="Event Photos"
        clearControlTooltipContent="Remove all photos"
        getUploadUrl={getUploadUrl}
        onClear={() => setValue('images', [])}
        onRemoveImagesAt={handleRemoveImagesAt}
        onImageUpload={handleImageUpload}
        multiple
      />
      <div className={styles.disclaimerMessage}>
        <div className={styles.iconContainer}>
          <Icon name="imageIcon" />
        </div>
        <div className={styles.messageContainer}>
          <p className={styles.messageTitle}>Photo is optional</p>
          <p className={styles.message}>
            Add one image to make your event more appealing. You can skip this
            step and continue.
          </p>
        </div>
      </div>
    </section>
  );
};
